import React, { useState } from 'react';
import AnimatedFeature from '../components/AnimatedFeature';
import { supabase } from '../lib/supabase';

const Home: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setErrorMessage('');

    try {
      const { error } = await supabase.from('waitlist').insert([{ email }]);

      if (error) throw error;

      setStatus('success');
      setEmail('');
    } catch (error) {
      setStatus('error');
      if (error instanceof Error) {
        setErrorMessage(
          error.message.includes('unique constraint')
            ? 'This email is already on the waitlist!'
            : 'Something went wrong. Please try again.'
        );
      }
    }
  };

  return (
    <div className="px-6 md:px-12">
      {/* Hero Section */}
      <div className="mx-auto mb-24 max-w-4xl">
        <h1 className="animate-fade-in mb-8 text-5xl font-bold md:text-7xl">
          Meet your digital companion that thinks like you do.
        </h1>
        <p
          className="animate-fade-in mb-16 text-2xl text-gray-400 md:text-3xl"
          style={{ animationDelay: '200ms' }}
        >
          We're reimagining personal computing. No more rigid interfaces or endless apps. Just an
          intuitive AI that understands you and adapts to your needs.
        </p>
      </div>

      {/* Features Grid */}
      <div className="mx-auto mb-24 grid max-w-6xl grid-cols-1 gap-12 md:grid-cols-2">
        {features.map((feature, index) => (
          <AnimatedFeature key={index} text={feature} delay={index * 100} />
        ))}
      </div>

      {/* Final CTA */}
      <div
        className="animate-fade-in mx-auto max-w-4xl pb-24 text-center"
        id="signup"
        style={{ animationDelay: '800ms' }}
      >
        <h2 className="mb-8 text-4xl font-bold md:text-5xl">
          Welcome to the future of personal computing.
          <span className="mt-4 block">Built for humans, by humans.</span>
        </h2>
        <form onSubmit={handleSubmit} className="mx-auto max-w-md">
          <div className="mb-4 flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={status === 'loading'}
              className="flex-1 border border-border bg-transparent px-4 py-3 text-foreground placeholder-gray-500 focus:border-gray-600 focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="bg-accent px-6 py-3 font-bold text-background transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status === 'loading' ? 'Joining...' : 'Join waitlist'}
            </button>
          </div>
          {status === 'success' && (
            <p className="mt-2 text-green-400">Thanks for joining! We'll be in touch soon.</p>
          )}
          {status === 'error' && <p className="mt-2 text-red-400">{errorMessage}</p>}
        </form>
      </div>
    </div>
  );
};

const features = [
  'Discovers the perfect hotel that everyone in your group will love',
  'Curates your guest list for the most memorable gatherings',
  'Plans your business trips with uncanny precision',
  'Sets the perfect mood with spot-on entertainment choices',
  'Anticipates which files you need before you ask',
  'Makes social planning effortless (and drama-free)',
  'Coordinates family trips that actually feel like vacation',
  'Finds meeting times that work like magic',
  'Creates the perfect soundtrack for every moment',
  "Works proactively, so you don't have to ask",
  'Zero app installations. Zero hassle.',
  'Keeps your digital life beautifully organized',
  'Protects your privacy like a vault',
  'Adapts to you, not the other way around',
];

export default Home;
